/**
 * Utilities for date parsing and age calculation
 */

const DATE_DOT_REGEX = /^(\d{2})\.(\d{2})\.(\d{4})$/
const DATE_ISO_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/

interface ParsedDate {
	day: number
	month: number
	year: number
}

/**
 * Parse a date string in DD.MM.YYYY or YYYY-MM-DD format
 * @param dateStr - Date string in format DD.MM.YYYY or YYYY-MM-DD
 * @returns Parsed date object or null if invalid format
 */
function parseDate(dateStr: string): ParsedDate | null {
	const dotMatch = DATE_DOT_REGEX.exec(dateStr)
	if (dotMatch) {
		const [, day, month, year] = dotMatch
		return toParsedDate(day, month, year)
	}

	const isoMatch = DATE_ISO_REGEX.exec(dateStr)
	if (isoMatch) {
		const [, year, month, day] = isoMatch
		return toParsedDate(day, month, year)
	}

	return null
}

function toParsedDate(
	day: string | undefined,
	month: string | undefined,
	year: string | undefined,
): ParsedDate | null {
	if (day === undefined || month === undefined || year === undefined) {
		return null
	}
	return {
		day: parseInt(day, 10),
		month: parseInt(month, 10),
		year: parseInt(year, 10),
	}
}

/**
 * Calculate age from a date string in DD.MM.YYYY or YYYY-MM-DD format
 * @param dateStr - Date string in format DD.MM.YYYY or YYYY-MM-DD
 * @returns Age in years, or null if date is invalid
 */
export function calculateAge(dateStr: string): number | null {
	const parsed = parseDate(dateStr)
	if (!parsed) return null

	const now = new Date()
	const birthDateThisYear = new Date(
		now.getFullYear(),
		parsed.month - 1,
		parsed.day,
	)
	const age = now.getFullYear() - parsed.year
	if (now < birthDateThisYear) return age - 1
	return age
}

/**
 * Validate a date string for registration
 * Checks format, valid calendar date, and age range (5-65)
 * @param dateStr - Date string in format DD.MM.YYYY
 * @returns true if date is valid for registration
 */
export function isValidDate(dateStr: string): boolean {
	if (!dateStr) return false

	const parsed = parseDate(dateStr)
	if (!parsed) return false

	const { day, month, year } = parsed
	const dt = new Date(year, month - 1, day)

	// Check if date is valid (not Feb 30, etc.)
	if (
		dt.getFullYear() !== year ||
		dt.getMonth() !== month - 1 ||
		dt.getDate() !== day
	) {
		return false
	}

	const age = calculateAge(dateStr)
	return age != null && age >= 5 && age <= 65
}
